"use client";

import { useChatId } from "@ai-sdk-tools/store";
import { useChatActions } from "@ai-sdk-tools/store";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { useChatInterface } from "@/hooks/use-chat-interface";
import Image from "next/image";

const SUGGESTIONS = [
  { toolChoice: "brain-basics", text: "Explain the 3 important parts of the brain", gif: "/brand/icons/spiral.gif", gradient: "from-[var(--mindup-blue)] to-[var(--mindup-navy)]", iconBg: "bg-[var(--mindup-blue-light)]" },
  { toolChoice: "activities", text: "Get a classroom activity", gif: "/brand/icons/flower.gif", gradient: "from-[var(--mindup-orange)] to-[var(--mindup-deep-orange)]", iconBg: "bg-[var(--mindup-orange-light)]" },
  { toolChoice: "mindfulness", text: "Recommend a mindful breathing exercise", gif: "/brand/icons/sun.gif", gradient: "from-[var(--mindup-green)] to-[var(--mindup-deep-green)]", iconBg: "bg-[var(--mindup-mint)]" },
];

type Suggestion = (typeof SUGGESTIONS)[number];

export function SuggestionPills() {
  const { sendMessage } = useChatActions();
  const { setChatId } = useChatInterface();
  const chatId = useChatId();

  const handleSuggestionClick = (suggestion: Suggestion) => {
    if (chatId) {
      setChatId(chatId);
    }

    sendMessage({
      text: suggestion.text,
      metadata: {
        toolChoice: suggestion.toolChoice,
      },
    });
  };

  return (
    <div className="flex flex-wrap gap-3 justify-center">
      {SUGGESTIONS.map((suggestion, index) => (
        <motion.div
          key={suggestion.text}
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.3,
            delay: 0.35 + index * 0.06,
            ease: "easeOut",
          }}
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <Button
            size="lg"
            onClick={() => handleSuggestionClick(suggestion)}
            className="group relative rounded-2xl text-xs sm:text-sm font-bold bg-white dark:bg-[#363636] border border-gray-200/80 dark:border-white/10 text-[var(--mindup-soft-black)] dark:text-white shadow-sm hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-black/30 hover:border-transparent transition-all duration-300 overflow-hidden px-4 py-2.5 sm:px-5 sm:py-3"
          >
            {/* Gradient background on hover */}
            <span className={`absolute inset-0 bg-gradient-to-r ${suggestion.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            
            {/* Content */}
            <span className="relative flex items-center gap-2.5">
              <span className={`w-7 h-7 rounded-lg ${suggestion.iconBg} group-hover:bg-white/20 p-1 shrink-0 transition-colors overflow-hidden`}>
                <Image src={suggestion.gif} alt="" width={20} height={20} className="w-full h-full" unoptimized />
              </span>
              <span className="group-hover:text-white transition-colors">{suggestion.text}</span>
            </span>
          </Button>
        </motion.div>
      ))}
    </div>
  );
}
